import { useToggle } from "@uidotdev/usehooks";
import { cn } from "cn";
import {
  Calendar,
  type CalendarProps,
} from "@/components/ui/calendar/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DISPLAY_DATE_SHORT,
  formatDate,
  setUTCTimeToTzMidnight,
} from "@/libs/date-fns";

type DateSelectProps = {
  value: string; // ISO string
  onChange: (date: string) => void;
  timeZone: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  calendarProps?: Omit<
    CalendarProps,
    "onSelect | selected | required | timeZone | mode"
  >;
};

export function DateSelect({
  value,
  onChange,
  timeZone,
  className,
  placeholder,
  disabled,
  calendarProps,
  ...props
}: DateSelectProps) {
  const [isOpen, toggleIsOpen] = useToggle(false);

  const onSelect = (date: Date) => {
    const newDate = new Date(date);
    onChange(newDate.toISOString());
    toggleIsOpen(false);
  };

  return (
    <Popover
      modal={true}
      open={isOpen && !disabled}
      onOpenChange={toggleIsOpen}
    >
      <PopoverTrigger disabled={disabled} className="group" asChild>
        <div
          role="combobox"
          className={cn(
            /* sorted */
            "flex grow items-center gap-2",
            "h-10",
            "px-3 py-2",
            "cursor-pointer rounded-lg",
            "outline outline-outline-dark-low",
            "aria-invalid:outline-outline-negative-max data-[state=open]:outline-outline-dark-max data-[state=open]:outline-[1.5px] group-disabled:cursor-not-allowed group-disabled:bg-bg-dark-low group-disabled:opacity-50",
            !value && "text-text-dark-min",
            className,
          )}
          {...props}
        >
          {value ? (
            <p
              className="line-clamp-1 break-all text-sm"
              title={formatDate(value, DISPLAY_DATE_SHORT, timeZone)}
            >
              {formatDate(value, DISPLAY_DATE_SHORT, timeZone)}
            </p>
          ) : (
            <p className="text-text-dark-min text-sm">
              {placeholder || "Select a day"}
            </p>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Calendar
          data-test="calendar"
          {...calendarProps}
          mode="single"
          selected={
            value
              ? new Date(setUTCTimeToTzMidnight(value, timeZone))
              : undefined
          }
          defaultMonth={
            value
              ? new Date(setUTCTimeToTzMidnight(value, timeZone))
              : undefined
          }
          onSelect={onSelect}
          timeZone={timeZone}
          required
          fixedWeeks
        />
      </PopoverContent>
    </Popover>
  );
}
